import React, { useRef, useState } from 'react';
import { Button, Grid, TextField, Typography } from '@material-ui/core';
import PropTypes from 'prop-types';
import { toJS } from 'mobx';
import ConfirmDialog from '../Shared/ConfirmDialog';

const allowedTypes = ['.pdf'];

const FileUpload = ({ FileHostStore, onUploadComplete, authStore }) => {
  const [fileContent, setFileContent] = useState(null);
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState('');
  const [fileNameAlreadyExist, setFileNameAlreadyExist] = useState(false);
  const warnedDuplicate = useRef(false);
  const fileInputRef = useRef();

  const resetForm = () => {
    setFileName('');
    setFileContent(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = async (event) => {
    setError('');
    const file = event.target.files[0];

    if (file) {
      setFileName(file.name);
      const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;
      if (!allowedTypes.includes(fileExtension)) {
        setError('Unsupported file type. Please upload a .pdf file.');
        setFileContent(null);
        return;
      }
      try {
        const arrayBuffer = await file.arrayBuffer();
        const byteArray = new Uint8Array(arrayBuffer);
        setFileContent(Array.from(byteArray));
      } catch (error) {
        setError('Failed to read file. Please try again.');
      }
    }
  };

  const handleUpload = async () => {
    if (!fileName) {
      setError('Please select a file to upload.');
      return;
    }

    const user = toJS(authStore.getUser);

    const userName = `${user?.result?.first_name} ${user?.result?.last_name}`;

    const fileData = {
      file_type: allowedTypes[0],
      file_name: fileName,
      uploaded_by: userName,
      file_content: fileContent,
      warned_duplicate: warnedDuplicate.current,
    };

    try {
      await FileHostStore.addCourse(authStore, fileData);
      if (onUploadComplete) {
        onUploadComplete(fileName);
      }
      return true;
    } catch (err) {
      const errorMessage = err.message || 'Failed to upload the file.';
      setError(errorMessage);
      setFileNameAlreadyExist(errorMessage.includes('Status 409 File name already exists.'));
      return false;
    }
  };

  const handleContinue = async () => {
    warnedDuplicate.current = true;
    const success = await handleUpload();
    if (success) {
      resetForm();
      setFileNameAlreadyExist(false);
    }
  };

  const handleClose = () => {
    setFileNameAlreadyExist(false);
    resetForm();
  };

  return (
    <Grid container direction="column" spacing={2} style={{ margin: '20px' }}>
      <Grid item>
        <label htmlFor="file-upload-input">
          <Typography variant="h6">Upload a New File</Typography>
        </label>
        <TextField
          id="file-upload-input"
          type="file"
          inputRef={fileInputRef}
          onChange={handleFileChange}
          inputProps={{ accept: 'application/pdf' }}
          style={{ display: 'block', marginTop: 8 }}
        />
      </Grid>
      <ConfirmDialog
        open={fileNameAlreadyExist}
        title="Duplicate File Name"
        description={`A file named '${fileName}' has already been uploaded. Are you sure you want to upload it again?`}
        handleClose={handleClose}
        handleContinue={handleContinue}
        cancelLabel="Cancel"
        continueLabel="Continue"
      />
      {error && !fileNameAlreadyExist && (
        <Grid item>
          <Typography color="error">{error}</Typography>
        </Grid>
      )}
      <Grid item>
        <Button variant="contained" color="primary" onClick={handleUpload} disabled={!fileContent}>
          Upload
        </Button>
      </Grid>
    </Grid>
  );
};

FileUpload.propTypes = {
  FileHostStore: PropTypes.object,
  onUploadComplete: PropTypes.func.isRequired,
  authStore: PropTypes.object.isRequired,
};

export default FileUpload;
